import { apiFetch } from './apiClient';
import type { Message } from '../components/Message';
import type { Source } from '../components/SourcesList';

/**
 * Shape returned by `GET /conversations` (per-row summary). Field names mirror
 * the server's `ConversationSummaryDto` exactly — no client-side rename.
 *
 * Notable difference from the original handoff doc: server uses `lastMessageAt`,
 * not `updatedAt`. The grouping logic below buckets by `lastMessageAt`.
 */
export interface ConversationSummary {
  threadKey: string;
  title: string | null;       // server falls back to the truncated first question; treat null as legacy/defensive
  createdAt: string;          // ISO
  lastMessageAt: string;      // ISO
}

/**
 * Per-assistant-turn metadata as the server actually serializes it on the
 * wire — a nested object under `metadata`, NOT a stringified JSON blob.
 * Matches `Kairos.Shared.DTO.Conversations.AssistantMessageMetadata` (camel-
 * cased by ASP.NET defaults). User turns omit this field.
 *
 * Sources are projected from `RetrievedChunkDto` — the SPA only reads `title`
 * and `webViewLink`; the other fields (location/score/snippet/isMetadataOnly)
 * arrive and are ignored. `webViewLink` is nullable on the server (e.g. a
 * RetrievedChunk built from a stub doc), so we mirror that nullability and
 * let SourcesList's defensive `iconNameFor` fall back to a globe icon.
 */
export interface ServerSource extends Source {
  // RetrievedChunkDto extras — not rendered, kept untyped to avoid claiming
  // a contract the SPA doesn't actually depend on.
  location?: string;
  score?: number;
  snippet?: string;
  isMetadataOnly?: boolean;
}

export interface ServerMessageMetadata {
  sources: ServerSource[];
  durationSeconds: number;
}

/**
 * Server-side message shape inside `GET /conversations/{threadKey}.messages`.
 * `role` is an int enum (0=User, 1=Assistant) per Kairos.Shared.Enums.ConversationRole.
 *
 * Earlier versions of this file expected `metadataJson: string` (stringified)
 * and ran `JSON.parse` on it. That was wrong — the server has always sent a
 * nested object — and meant assistant source-citations silently never
 * rendered on resumed conversations. Security audit finding CF.1 (2026-05-24).
 */
interface ServerConversationMessage {
  turnNumber: number;
  role: 0 | 1;
  content: string;
  metadata: ServerMessageMetadata | null;
  createdAt: string;
}

interface ServerConversationDetail extends ConversationSummary {
  verbosity: string;
  messages: ServerConversationMessage[];
}

/** Detail shape the SPA consumes — messages already projected into the SPA's Message shape. */
export interface Conversation {
  threadKey: string;
  title: string | null;
  createdAt: string;
  lastMessageAt: string;
  verbosity: string;
  messages: Message[];
}

/**
 * Project a persisted server message into the SPA's `Message` shape.
 *
 * Important: assistant `sources` live on `Message.sources` (top-level — that's
 * what `MessageBody` feeds to `SourcesList`), while the live SSE-derived
 * `Message.meta = { threadKey, turn, verbosity, durationSeconds }` is what
 * `MessageBody` uses both as the "turn complete" gate (`isComplete = !!meta`)
 * AND as the data for the footer `<Meta />` row. So a resumed assistant turn
 * needs BOTH populated, or sources never render even when present.
 *
 * Exported for unit tests; production callers should use `getConversation`.
 */
export function projectMessage(m: ServerConversationMessage, threadKey: string): Message {
  const isAssistant = m.role === 1;
  const md = isAssistant ? m.metadata : null;
  return {
    id: `${threadKey}-${m.turnNumber}-${isAssistant ? 'a' : 'u'}`,
    role: isAssistant ? 'assistant' : 'user',
    content: m.content,
    // NOT 'done' — a non-empty status puts AssistantHeader into "is-streaming"
    // mode (header label becomes "Done" with three animated dots after it),
    // which is the wrong affordance for a resumed historical turn. Status is
    // a live-stream indicator: Thinking / Working / Searching documents / etc.
    // A persisted, fully-rendered turn is not in flight, so it should not
    // carry a status at all.
    status: undefined,
    sources: md?.sources,
    // Set `meta` whenever metadata is present so `isComplete` is true and the
    // SourcesList renders. `turn` matches the SSE `meta` event's `turn`, so a
    // resumed row looks the same as a freshly-streamed one to downstream UI.
    // `threadKey` and `verbosity` are not persisted per-message (they live on
    // the parent Conversation row), so we leave them undefined here — `<Meta />`
    // is defensive about all four fields being optional.
    meta: md ? { turn: m.turnNumber, durationSeconds: md.durationSeconds } : undefined,
  } as Message;
}

export async function listConversations(token: string, search?: string): Promise<ConversationSummary[]> {
  const path = search ? `/conversations?search=${encodeURIComponent(search)}` : '/conversations';
  const r = await apiFetch(path, { token });
  if (!r.ok) throw new Error(`listConversations: ${r.status}`);
  return r.json();
}

export async function getConversation(token: string, threadKey: string): Promise<Conversation> {
  const r = await apiFetch(`/conversations/${encodeURIComponent(threadKey)}`, { token });
  if (!r.ok) throw new Error(`getConversation: ${r.status}`);
  const raw = (await r.json()) as ServerConversationDetail;
  return {
    threadKey: raw.threadKey,
    title: raw.title,
    createdAt: raw.createdAt,
    lastMessageAt: raw.lastMessageAt,
    verbosity: raw.verbosity,
    messages: raw.messages.map(m => projectMessage(m, raw.threadKey)),
  };
}

export async function renameConversation(token: string, threadKey: string, title: string): Promise<void> {
  const r = await apiFetch(`/conversations/${encodeURIComponent(threadKey)}`, {
    token,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  if (!r.ok) throw new Error(`renameConversation: ${r.status}`);
}

export async function deleteConversation(token: string, threadKey: string): Promise<void> {
  const r = await apiFetch(`/conversations/${encodeURIComponent(threadKey)}`, {
    token,
    method: 'DELETE',
  });
  if (!r.ok && r.status !== 404) throw new Error(`deleteConversation: ${r.status}`);
}

/** Sidebar grouping: Today / Yesterday / Last 7 days / Earlier — driven by `lastMessageAt`. */
export interface ConversationGroup {
  label: 'Today' | 'Yesterday' | 'Last 7 days' | 'Earlier';
  items: ConversationSummary[];
}

export function groupConversations(rows: ConversationSummary[], now = Date.now()): ConversationGroup[] {
  const day = 24 * 60 * 60 * 1000;
  const todayStart = new Date(now).setHours(0, 0, 0, 0);
  const yesterdayStart = todayStart - day;
  const sevenStart = todayStart - 7 * day;

  const buckets: Record<ConversationGroup['label'], ConversationSummary[]> = {
    'Today': [],
    'Yesterday': [],
    'Last 7 days': [],
    'Earlier': [],
  };

  for (const r of rows) {
    const t = Date.parse(r.lastMessageAt);
    if (t >= todayStart) buckets.Today.push(r);
    else if (t >= yesterdayStart) buckets.Yesterday.push(r);
    else if (t >= sevenStart) buckets['Last 7 days'].push(r);
    else buckets.Earlier.push(r);
  }

  return (['Today', 'Yesterday', 'Last 7 days', 'Earlier'] as const)
    .map(label => ({ label, items: buckets[label] }))
    .filter(g => g.items.length > 0);
}
